import React from 'react';
import { Stack, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import { useTranslation } from 'react-i18next';
import {
    formatDisplayAmount,
    isIncomeTransaction,
    transactionAmountSx,
    TRANSACTION_EXPENSE,
} from '../../utils/transactionAmount';

const INCOME_TOGGLE_SX = {
    '&.Mui-selected': {
        color: 'success.main',
        fontWeight: 700,
    },
};

function directionLabelKey(direction) {
    return isIncomeTransaction(direction)
        ? 'app.addTransaction.form.directionIncome'
        : 'app.addTransaction.form.directionExpense';
}

function DirectionToggleLabel({ direction }) {
    const { t } = useTranslation();
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <TransactionDirectionIcon direction={direction} size="small" showTooltip={false} />
            <span>{t(directionLabelKey(direction))}</span>
        </Stack>
    );
}

/** Incoming (green) vs outgoing (red) arrow for income vs expense. */
export function TransactionDirectionIcon({
    direction,
    size = 'small',
    showTooltip = true,
}) {
    const { t } = useTranslation();
    const income = isIncomeTransaction(direction);
    const label = income ? t('app.transactions.income') : t('app.transactions.expense');
    const icon = income
        ? <CallReceivedIcon color="success" fontSize={size} />
        : <CallMadeIcon color="error" fontSize={size} />;

    if (!showTooltip) return icon;
    return <Tooltip title={label}>{icon}</Tooltip>;
}

/** Positive amount with income (green) or expense (neutral) styling — tables and inline text. */
export function TransactionAmountText({
    amount,
    direction,
    formatMagnitude,
    variant = 'body2',
    fontWeight,
    sx = {},
    component,
}) {
    return (
        <Typography
            component={component}
            variant={variant}
            sx={{ fontWeight, ...transactionAmountSx(direction), ...sx }}
        >
            {formatDisplayAmount(amount, direction, formatMagnitude)}
        </Typography>
    );
}

/** Non-table contexts: colored positive amount plus directional arrow. */
export function TransactionAmountWithDirection({
    amount,
    direction,
    formatMagnitude,
    variant = 'body2',
    iconSize = 'small',
    spacing = 0.75,
    fontWeight = 600,
    sx,
    align = 'center',
}) {
    return (
        <Stack direction="row" spacing={spacing} alignItems={align} sx={sx}>
            <TransactionDirectionIcon direction={direction} size={iconSize} />
            <TransactionAmountText
                amount={amount}
                direction={direction}
                formatMagnitude={formatMagnitude}
                variant={variant}
                fontWeight={fontWeight}
            />
        </Stack>
    );
}

/** Table column header: dual arrows with tooltip (no "Direction" text). */
export function TransactionDirectionColumnHeader() {
    const { t } = useTranslation();
    return (
        <Tooltip title={t('app.addTransaction.form.direction')}>
            <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                justifyContent="center"
                component="span"
            >
                <CallMadeIcon color="error" sx={{ fontSize: 16 }} />
                <CallReceivedIcon color="success" sx={{ fontSize: 16 }} />
            </Stack>
        </Tooltip>
    );
}

/** Read-only: colored arrow + Expense/Income (no "Direction" prefix). */
export function TransactionDirectionBadge({
    direction,
    variant = 'body2',
    fontWeight = 500,
}) {
    const { t } = useTranslation();
    const resolved = direction || TRANSACTION_EXPENSE;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <TransactionDirectionIcon direction={resolved} size="small" showTooltip={false} />
            <Typography
                variant={variant}
                fontWeight={fontWeight}
                sx={transactionAmountSx(resolved) || { color: 'text.primary' }}
            >
                {t(directionLabelKey(resolved))}
            </Typography>
        </Stack>
    );
}

/** Expense/Income selector with colored arrows on each option. */
export function TransactionDirectionToggleGroup({
    value,
    onChange,
    disabled = false,
    fullWidth = false,
    size = 'small',
}) {
    const resolved = value || TRANSACTION_EXPENSE;
    return (
        <ToggleButtonGroup
            exclusive
            size={size}
            fullWidth={fullWidth}
            value={resolved}
            onChange={(event, next) => {
                if (next) onChange(next);
            }}
            disabled={disabled}
        >
            <ToggleButton value="expense">
                <DirectionToggleLabel direction="expense" />
            </ToggleButton>
            <ToggleButton value="income" sx={INCOME_TOGGLE_SX}>
                <DirectionToggleLabel direction="income" />
            </ToggleButton>
        </ToggleButtonGroup>
    );
}

/** Table column: direction arrow only (amount lives in the Amount column). */
export function TransactionDirectionCell({ direction, align = 'center' }) {
    return (
        <Stack direction="row" alignItems="center" justifyContent={align} sx={{ width: '100%' }}>
            <TransactionDirectionIcon direction={direction} />
        </Stack>
    );
}
